import { AssistantProposalActionBar } from "@/components/assistant/AssistantProposalActionBar";
import type { ApprovalBarProps } from "@/components/assistant/useAssistantWriteApprovalHandlers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DraftTemplateSectionList } from "@/components/draft-templates/DraftTemplateSectionList";
import {
  buildTemplateStructure,
  formatSectionsForDisplay,
  parseTemplateStructure,
  sectionsForSave,
  structureToJsonString,
  templateSectionsToEditorRows,
  type TemplateSectionEditorRow,
} from "@/components/draft-templates/draft-template-sections";
import { cn } from "@/lib/utils";
import type { DraftTemplate } from "@/types/drafting";
import { useMemo, useState } from "react";
import type { DraftTemplateProposalInput } from "./types";

type Props = {
  mode: "create" | "update";
  proposal: DraftTemplateProposalInput;
  existing?: DraftTemplate | null;
  disabled?: boolean;
  showActions?: boolean;
  approvalBar?: ApprovalBarProps;
  embedded?: boolean;
  onApprove?: () => void;
  onEditSubmit?: (edited: DraftTemplateProposalInput) => void;
};

function FieldRow({
  label,
  value,
  changed,
  editing,
  editValue,
  onEditValue,
  multiline,
}: {
  label: string;
  value: string;
  changed: boolean;
  editing: boolean;
  editValue: string;
  onEditValue: (v: string) => void;
  multiline?: boolean;
}) {
  return (
    <div className="grid gap-1.5 sm:grid-cols-[8.5rem_1fr] sm:items-start">
      <span className="text-xs font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-400">
        {label}
      </span>
      {editing ? (
        multiline ? (
          <Textarea
            value={editValue}
            onChange={(e) => onEditValue(e.target.value)}
            rows={6}
            className="min-h-[5rem] font-mono text-xs"
          />
        ) : (
          <Input value={editValue} onChange={(e) => onEditValue(e.target.value)} className="text-sm" />
        )
      ) : (
        <p
          className={cn(
            "break-words whitespace-pre-wrap text-[15px] leading-snug text-[var(--lm-foreground-strong)] dark:text-slate-100",
            multiline && "font-mono text-xs",
            changed && "rounded-md bg-amber-500/10 px-2 py-1 ring-1 ring-amber-500/30"
          )}
        >
          {value || "—"}
        </p>
      )}
    </div>
  );
}

export function DraftTemplateProposalCard({
  mode,
  proposal,
  existing,
  disabled,
  showActions = true,
  approvalBar,
  embedded = false,
  onApprove,
  onEditSubmit,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(proposal.name ?? "");
  const [draftValue, setDraftValue] = useState(proposal.value ?? "");
  const [draftBlurb, setDraftBlurb] = useState(proposal.blurb ?? "");
  const [draftVersion, setDraftVersion] = useState(proposal.version ?? "v1.0");
  const [draftSectionRows, setDraftSectionRows] = useState<TemplateSectionEditorRow[]>(() =>
    templateSectionsToEditorRows(parseTemplateStructure(proposal.structure))
  );

  const title = mode === "create" ? "Create template" : "Update template";
  const structureDisplay = formatSectionsForDisplay(proposal.structure);

  const changed = useMemo(
    () => ({
      name: existing ? (proposal.name ?? "") !== existing.name : false,
      value: existing ? (proposal.value ?? "") !== existing.value : false,
      blurb: existing ? (proposal.blurb ?? "") !== (existing.blurb ?? "") : false,
      version: existing ? (proposal.version ?? "") !== existing.version : false,
      structure:
        existing != null
          ? structureToJsonString(proposal.structure) !== structureToJsonString(existing.structure)
          : false,
    }),
    [existing, proposal]
  );

  function startEdit() {
    setDraftName(proposal.name ?? "");
    setDraftValue(proposal.value ?? "");
    setDraftBlurb(proposal.blurb ?? "");
    setDraftVersion(proposal.version ?? "v1.0");
    setDraftSectionRows(templateSectionsToEditorRows(parseTemplateStructure(proposal.structure)));
    setEditing(true);
  }

  function submitEdit() {
    const structure = buildTemplateStructure(sectionsForSave(draftSectionRows));
    onEditSubmit?.({
      id: proposal.id,
      name: draftName.trim(),
      value: draftValue.trim(),
      blurb: draftBlurb.trim(),
      version: draftVersion.trim(),
      structure,
    });
    setEditing(false);
  }

  return (
    <div
      className={cn(
        "overflow-hidden bg-white dark:bg-white/10",
        embedded
          ? "border-0 shadow-none"
          : "rounded-xl border border-[rgba(15,23,42,0.09)] shadow-[var(--shadow-card)] dark:border-white/10 dark:shadow-none"
      )}
    >
      {embedded ? null : (
        <div className="border-b border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] px-4 py-2.5 dark:border-white/10 dark:bg-white/5">
          <p className="text-sm font-semibold text-[var(--lm-foreground-strong)]">{title}</p>
        </div>
      )}
      <div className="space-y-3 px-4 py-3">
        <FieldRow
          label="Name"
          value={proposal.name ?? ""}
          changed={changed.name}
          editing={showActions && editing}
          editValue={draftName}
          onEditValue={setDraftName}
        />
        <FieldRow
          label="Short code"
          value={proposal.value ?? ""}
          changed={changed.value}
          editing={showActions && editing}
          editValue={draftValue}
          onEditValue={setDraftValue}
        />
        <FieldRow
          label="Blurb"
          value={proposal.blurb ?? ""}
          changed={changed.blurb}
          editing={showActions && editing}
          editValue={draftBlurb}
          onEditValue={setDraftBlurb}
          multiline
        />
        <FieldRow
          label="Version"
          value={proposal.version ?? "v1.0"}
          changed={changed.version}
          editing={showActions && editing}
          editValue={draftVersion}
          onEditValue={setDraftVersion}
        />
        {showActions && editing ? (
          <DraftTemplateSectionList
            label="Structure"
            idPrefix="proposal-template-section"
            rows={draftSectionRows}
            disabled={disabled}
            onChange={setDraftSectionRows}
          />
        ) : (
          <div className="grid gap-1.5 sm:grid-cols-[8.5rem_1fr] sm:items-start">
            <span className="text-xs font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-400">
              Structure
            </span>
            <p
              className={cn(
                "break-words whitespace-pre-wrap text-[15px] leading-snug text-[var(--lm-foreground-strong)] dark:text-slate-100",
                changed.structure && "rounded-md bg-amber-500/10 px-2 py-1 ring-1 ring-amber-500/30"
              )}
            >
              {structureDisplay}
            </p>
          </div>
        )}
      </div>
      {!embedded && approvalBar ? (
        <AssistantProposalActionBar mode={mode} {...approvalBar} />
      ) : showActions ? (
        <div className="flex flex-wrap justify-end gap-2 border-t border-[var(--lm-border-subtle)] px-4 py-3 dark:border-white/10">
          {editing ? (
            <>
              <Button type="button" variant="surface" size="sm" onClick={() => setEditing(false)} disabled={disabled}>
                Cancel
              </Button>
              <Button type="button" variant="default" size="sm" onClick={submitEdit} disabled={disabled}>
                Apply edits
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="surface" size="sm" onClick={startEdit} disabled={disabled}>
                Edit
              </Button>
              <Button type="button" variant="default" size="sm" onClick={() => onApprove?.()} disabled={disabled}>
                {mode === "create" ? "Approve & Create" : "Approve & Update"}
              </Button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
